import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, Platform, Text, View } from "react-native";
import { WebView } from "react-native-webview";

export type EventCoordinates = Readonly<{
  lat: number;
  lng: number;
}>;

type EventLocationMapProps = Readonly<{
  location: string;
  coordinates?: EventCoordinates;
  onLocationSelect?: (
    locationName: string,
    coordinates: EventCoordinates,
  ) => void;
}>;

const DEFAULT_COORDS: EventCoordinates = { lat: 27.4712, lng: 89.6339 }; // Thimphu

const reverseGeocode = async (lat: number, lng: number): Promise<string> => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
    );
    const json = await res.json();
    const { amenity, road, neighbourhood, city, state, country } =
      json.address ?? {};

    const placeName =
      json.name ?? amenity ?? road ?? neighbourhood ?? city ?? state ?? "";
    const location = city ?? state ?? "";
    const suffix = country ? `, ${country}` : "";

    if (placeName && location && placeName !== location) {
      return `${placeName}, ${location}${suffix}`;
    }
    return `${placeName || location}${suffix}` || json.display_name;
  } catch {
    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  }
};

const buildLeafletHtml = (coords: EventCoordinates, interactive: boolean) => `
  <!DOCTYPE html>
  <html>
    <head>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
      <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body, #map { width: 100%; height: 100%; }
      </style>
    </head>
    <body>
      <div id="map"></div>
      <script>
        const map = L.map('map', {
          zoomControl: true,
          attributionControl: false,
        }).setView([${coords.lat}, ${coords.lng}], 16);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(map);

        const marker = L.marker([${coords.lat}, ${coords.lng}], {
          draggable: ${interactive},
        }).addTo(map);

        function sendLocation(lat, lng) {
          const msg = JSON.stringify({ lat, lng });
          // Works for both WebView (mobile) and parent iframe (web)
          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(msg);
          } else if (window.parent !== window) {
            window.parent.postMessage(msg, '*');
          }
        }

        ${
          interactive
            ? `
          map.on('click', (e) => {
            marker.setLatLng(e.latlng);
            sendLocation(e.latlng.lat, e.latlng.lng);
          });

          marker.on('dragend', () => {
            const pos = marker.getLatLng();
            sendLocation(pos.lat, pos.lng);
          });
        `
            : ""
        }
      </script>
    </body>
  </html>
`;

const WebMap = ({ coordinates, onLocationSelect }: EventLocationMapProps) => {
  const { t } = useTranslation();
  const coords = coordinates ?? DEFAULT_COORDS;
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [loading, setLoading] = useState(false);
  const interactive = !!onLocationSelect;

  useEffect(() => {
    const handleMessage = async (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        if (typeof data.lat !== "number") return;
        setLoading(true);
        const name = await reverseGeocode(data.lat, data.lng);
        setLoading(false);
        onLocationSelect?.(name, { lat: data.lat, lng: data.lng });
      } catch {}
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onLocationSelect]);

  return (
    <View className="w-full h-48 rounded-t-xl overflow-hidden">
      <iframe
        ref={iframeRef}
        srcDoc={buildLeafletHtml(coords, interactive)}
        style={{ width: "100%", height: "100%", border: 0 }}
        sandbox="allow-scripts"
      />
      {loading && (
        <View className="absolute bottom-2 left-0 right-0 items-center">
          <View className="bg-white/90 rounded-full px-3 py-1 flex-row items-center gap-2">
            <ActivityIndicator size="small" />
            <Text className="text-xs text-gray-dark">
              {t("LABEL_LOCATING")}...
            </Text>
          </View>
        </View>
      )}
      {interactive && !loading && (
        <View className="absolute bottom-2 right-2">
          <View className="bg-white/90 rounded-full px-3 py-1">
            <Text className="text-xs text-primary font-medium">
              {t("HINT_TAP_OR_DRAG_PIN")}
            </Text>
          </View>
        </View>
      )}
    </View>
  );
};

const MobileMap = ({
  coordinates,
  onLocationSelect,
}: EventLocationMapProps) => {
  const { t } = useTranslation();
  const coords = coordinates ?? DEFAULT_COORDS;
  const [loading, setLoading] = useState(false);
  const interactive = !!onLocationSelect;

  const handleMessage = async (event: any) => {
    try {
      const data = JSON.parse(event.nativeEvent.data);
      if (typeof data.lat !== "number") return;
      setLoading(true);
      const name = await reverseGeocode(data.lat, data.lng);
      setLoading(false);
      onLocationSelect?.(name, { lat: data.lat, lng: data.lng });
    } catch {}
  };

  return (
    <View className="w-full h-48 rounded-t-xl overflow-hidden">
      <WebView
        source={{ html: buildLeafletHtml(coords, interactive) }}
        style={{ flex: 1 }}
        javaScriptEnabled
        scrollEnabled={false}
        onMessage={handleMessage}
      />
      {loading && (
        <View className="absolute bottom-2 left-0 right-0 items-center">
          <View className="bg-white/90 rounded-full px-3 py-1 flex-row items-center gap-2">
            <ActivityIndicator size="small" />
            <Text className="text-xs text-gray-dark">
              {t("LABEL_LOCATING")}...
            </Text>
          </View>
        </View>
      )}
      {interactive && !loading && (
        <View className="absolute bottom-2 right-2">
          <View className="bg-white/90 rounded-full px-3 py-1">
            <Text className="text-xs text-primary font-medium">
              {t("HINT_TAP_OR_DRAG_PIN")}
            </Text>
          </View>
        </View>
      )}
    </View>
  );
};

const MapPreview = (props: EventLocationMapProps) => {
  if (Platform.OS === "web") return <WebMap {...props} />;
  return <MobileMap {...props} />;
};

export default MapPreview;
